import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getPostForPreview } from '../../utils/blogHelpers';

const BlogPreview = () => {
  const { postId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { post: fetched } = await getPostForPreview(postId, token);
      if (!cancelled) {
        setPost(fetched);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [postId, token]);

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-white">
        <p className="text-muted-foreground text-sm">Loading preview…</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-white px-4">
        <div className="text-center max-w-sm">
          <h1 className="text-lg font-semibold text-foreground mb-2">Preview not found</h1>
          <p className="text-sm text-muted-foreground">
            This preview link is invalid, expired, or the post has been removed.
          </p>
        </div>
      </div>
    );
  }

  const statusLabel =
    post.status === 'scheduled' ? 'Scheduled' : post.status === 'published' ? 'Published' : 'Draft';

  return (
    <>
      <Helmet>
        <title>{post.title || 'Preview'} | Nesolearn Blog Preview</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen w-full bg-white">
        <div className="sticky top-0 z-10 border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-xs font-medium text-amber-800">
          Preview only — {statusLabel}, not visible on the public blog
          {post.status === 'published' && post.slug && (
            <>
              {' · '}
              <Link to={`/blog/${post.slug}`} className="underline">
                View the live version
              </Link>
            </>
          )}
        </div>

        <main className="max-w-2xl mx-auto px-4 md:px-8 pt-12 pb-24">
          {post.coverImageUrl && (
            <img src={post.coverImageUrl} alt="" className="w-full aspect-video object-cover rounded-lg mb-8" />
          )}
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{post.category}</p>
          <h1 className="text-3xl font-bold text-foreground mb-4">{post.title || 'Untitled'}</h1>
          <p className="text-muted-foreground mb-8">{post.excerpt}</p>

          {post.type === 'quote' ? (
            <blockquote className="text-xl italic border-l-4 border-border pl-4">{post.quoteText}</blockquote>
          ) : (
            <div className="prose prose-neutral max-w-none" dangerouslySetInnerHTML={{ __html: post.contentBody || '' }} />
          )}
        </main>
      </div>
    </>
  );
};

export default BlogPreview;